import React, { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeftIcon, Loader2Icon, XIcon } from "lucide-react";
import type { BookingPrefill } from "../../types/cafe";
import { STEPS, useBookingForm } from "./useBookingForm";
import { BookingDetailsStep } from "./BookingDetailsStep";
import { BookingScheduleStep } from "./BookingScheduleStep";
import { BookingContactStep } from "./BookingContactStep";
import { BookingConfirmation } from "./BookingConfirmation";
interface BookingModalProps {
  open: boolean;
  prefill: BookingPrefill;
  onClose: () => void;
}
const ease = [0.23, 1, 0.32, 1] as const;
export function BookingModal({
  open,
  prefill,
  onClose
}: BookingModalProps) {
  const booking = useBookingForm(prefill, open);
  const {
    step,
    status
  } = booking;
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    panelRef.current?.focus();
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);
  const title = booking.form.type === "activity" ? "Pesan aktivitas" : "Reservasi meja";
  return <AnimatePresence data-id="element-102" data-mp="325y8unby0aij">
      {open && <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-6" data-id="element-103" data-mp="323lj3nzy0vux">
          <motion.div className="absolute inset-0 bg-espresso/60" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={onClose} aria-hidden="true" data-id="element-104" data-mp="2x8pqomqso6ha" />
          <motion.div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby="booking-title" tabIndex={-1} initial={{
        opacity: 0,
        y: 24,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 24,
        scale: 0.98
      }} transition={{
        duration: 0.25,
        ease
      }} className="relative flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-2xl bg-cream shadow-2xl outline-none sm:max-w-xl sm:rounded-card" data-id="element-105" data-mp="rbkc92luwe2c">
            <div className="flex items-start justify-between gap-4 border-b border-cream-line px-6 pb-5 pt-6" data-id="element-106" data-mp="1p87kkk1xxi0a">
              <div data-id="element-107" data-mp="1t7gepq0w7hwd">
                <h2 id="booking-title" className="text-xl font-semibold tracking-tight text-ink" data-id="element-108" data-mp="1t7yegwmrke91">
                  {status === "success" ? "Pesanan berhasil" : title}
                </h2>
                {status !== "success" && <ol className="mt-4 flex gap-2" aria-label="Progres pemesanan" data-id="element-109" data-mp="3u31tcepsljn">
                    {STEPS.map((label, i) => <li key={label} className="flex items-center gap-2" data-id="element-110" data-mp="8txrjcmr7534" data-mpi={i}>
                        <span className={`h-1 w-10 rounded-full transition-colors duration-200 sm:w-14 ${i <= step ? "bg-sage" : "bg-cream-line"}`} aria-hidden="true" data-id="element-111" data-mp="18vmt09348ld" />
                        <span className={`text-xs font-medium ${i === step ? "text-ink" : "text-ink-muted"}`} aria-current={i === step ? "step" : undefined} data-id="element-112" data-mp="5agywjdsoiui">
                          {label}
                        </span>
                      </li>)}
                  </ol>}
              </div>
              <button type="button" onClick={onClose} className="-mr-2 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-ink-muted transition-colors duration-150 hover:bg-cream-dark hover:text-ink" aria-label="Tutup pemesanan" data-id="element-113" data-mp="3ipmxz6rysf8c">
                <XIcon className="h-5 w-5" data-id="element-114" data-mp="33phr26d8w5q6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-115" data-mp="38o2ro6wesrf5">
              <AnimatePresence mode="wait" initial={false} data-id="element-116" data-mp="3dme1wiqm93xw">
                <motion.div key={status === "success" ? "done" : step} initial={{
              opacity: 0,
              x: 12
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -12
            }} transition={{
              duration: 0.18,
              ease
            }} data-id="element-117" data-mp="3dkt66pskfeyg">
                  {status === "success" ? <BookingConfirmation form={booking.form} code={booking.code} data-id="element-118" data-mp="6y7rtcjoa7i" /> : step === 0 ? <BookingDetailsStep form={booking.form} errors={booking.errors} update={booking.update} data-id="element-119" data-mp="54zr8fdvubji" /> : step === 1 ? <BookingScheduleStep form={booking.form} errors={booking.errors} update={booking.update} dates={booking.dates} data-id="element-120" data-mp="26xnx6whwvd2z" /> : <BookingContactStep form={booking.form} errors={booking.errors} update={booking.update} data-id="element-121" data-mp="ja9ve38pv9gq" />}
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-cream-line bg-cream px-6 py-4" data-id="element-122" data-mp="aczi6ccc9a96">
              {status === "success" ? <button type="button" onClick={onClose} className="w-full rounded-full bg-espresso py-3 text-sm font-semibold text-cream transition-[background-color,transform] duration-150 ease-out hover:bg-espresso-light active:scale-[0.98]" data-id="element-123" data-mp="12zk7765cda9k">
                  Selesai
                </button> : <>
                  {step > 0 ? <button type="button" onClick={booking.back} className="inline-flex items-center gap-1.5 rounded-full px-3 py-2.5 text-sm font-medium text-ink-muted transition-colors duration-150 hover:text-ink" data-id="element-124" data-mp="3dqk2zn2ex5kf">
                      <ArrowLeftIcon className="h-4 w-4" data-id="element-125" data-mp="36agj3a6g6arm" /> Kembali
                    </button> : <span data-id="element-126" data-mp="2wij4ruk572oo" />}
                  {step < STEPS.length - 1 ? <button type="button" onClick={booking.next} className="rounded-full bg-sage px-7 py-3 text-sm font-semibold text-cream transition-[background-color,transform] duration-150 ease-out hover:bg-sage-dark active:scale-[0.97]" data-id="element-127" data-mp="2wgpzojp6gqis">
                      Lanjut
                    </button> : <button type="button" onClick={booking.submit} disabled={status === "submitting"} className="inline-flex items-center gap-2 rounded-full bg-sage px-7 py-3 text-sm font-semibold text-cream transition-[background-color,transform] duration-150 ease-out hover:bg-sage-dark active:scale-[0.97] disabled:opacity-70" data-id="element-128" data-mp="2iii5uknfu5bu">
                      {status === "submitting" && <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-129" data-mp="23y5lypbh1qfc" />}
                      {status === "submitting" ? "Memproses…" : "Konfirmasi pesanan"}
                    </button>}
                </>}
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}